// React
import { useMemo, useState } from 'react';

// Store
import useProfileStore from '../../store/profileStore.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Components
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import { CloseOutlined, PersonOutlined, SearchOutlined } from '@mui/icons-material';

// Style
import { grey, blue, orange } from '@mui/material/colors/index.js';
import { defaultColorScheme, tealColorScheme, darkTheme } from '../../styles/colorSchemes/index.js';

// Other
import { formatDate2, formatTrainingFocus, getConsistentAvatarColor } from '../../utils/index.js';

const FeedbackNotificationsDialog = (props) => {
    const { open, onClose, notifications, loading, onDismissAll, onDismissOne, onOpenNotification } = props;
    const { isDarkMode } = useAppTheme();
    const { colorScheme } = useProfileStore();

    const [query, setQuery] = useState('');
    const [selectedAthlete, setSelectedAthlete] = useState(null);

    const athleteOptions = useMemo(() => {
        const seen = new Set();
        const unique = [];

        for (const n of (notifications || [])) {
            const email = n?.athlete?.email;
            if (!email || seen.has(email)) continue;
            seen.add(email);

            unique.push({
                email,
                firstName: n?.athlete?.firstName || '',
                lastName: n?.athlete?.lastName || '',
            });
        }

        return unique.sort((a, b) => String(a?.lastName || '').localeCompare(String(b?.lastName || '')));
    }, [notifications]);

    const getAthleteLabel = (option) => {
        if (!option) return '';
        const name = `${option.lastName || ''}, ${option.firstName || ''}`.trim().replace(/^,\s*/, '');
        return name || option.email || '';
    };

    const inputValue = selectedAthlete && !query ? getAthleteLabel(selectedAthlete) : query;

    const filtered = useMemo(() => {
        const list = notifications || [];

        if (selectedAthlete?.email) {
            return list.filter(n => n?.athlete?.email === selectedAthlete.email);
        }

        const q = String(query || '').trim().toLowerCase();
        if (!q) return list;

        return list.filter(n => {
            const fullName = `${n?.athlete?.firstName || ''} ${n?.athlete?.lastName || ''}`.trim().toLowerCase();
            const email = String(n?.athlete?.email || '').toLowerCase();
            return fullName.includes(q) || email.includes(q);
        });
    }, [notifications, query, selectedAthlete]);

    let colors = defaultColorScheme.trainingFocusColor;
    if (colorScheme === 'teal') {
        colors = tealColorScheme.trainingFocusColor;
    }

    const canDismissAll = !loading && (notifications && notifications.length > 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    bgcolor: isDarkMode ? darkTheme.accordionBg : grey[50],
                    border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[200]}`,
                    boxShadow: isDarkMode
                        ? '0 18px 70px rgba(0, 0, 0, 0.65)'
                        : '0 18px 70px rgba(0, 0, 0, 0.25)',
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.25,
                    py: 1.75,
                    bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                    borderBottom: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[200]}`,
                    color: isDarkMode ? darkTheme.textPrimary : grey[900],
                    fontWeight: 700,
                }}
            >
                Neueste Rückmeldungen
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: isDarkMode ? darkTheme.textSecondary : grey[700],
                        bgcolor: isDarkMode ? darkTheme.headerBg : grey[100],
                        border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[200]}`,
                        '&:hover': {
                            bgcolor: isDarkMode ? darkTheme.cardHover : grey[200],
                        }
                    }}
                >
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2.25 }}>
                <Box
                    sx={{
                        mt: 1,
                        display: 'flex',
                        gap: 1.25,
                        alignItems: 'flex-end',
                        mb: 2,
                        p: 1.25,
                        borderRadius: 2.5,
                        bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                        border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[200]}`,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Autocomplete
                            size="small"
                            fullWidth
                            options={athleteOptions}
                            value={selectedAthlete}
                            inputValue={inputValue}
                            onInputChange={(_, value, reason) => {
                                if (reason === 'input') {
                                    setQuery(value);
                                    if (selectedAthlete) setSelectedAthlete(null);
                                    return;
                                }

                                if (reason === 'clear') {
                                    setQuery('');
                                    setSelectedAthlete(null);
                                    return;
                                }

                                // 'reset' happens after selecting an option: keep selection
                                if (reason === 'reset') {
                                    setQuery('');
                                }
                            }}
                            onChange={(_, value) => {
                                setSelectedAthlete(value || null);
                                setQuery('');
                            }}
                            getOptionLabel={getAthleteLabel}
                            isOptionEqualToValue={(option, value) => option?.email === value?.email}
                            noOptionsText={query ? 'Keine Athleten gefunden' : 'Keine Athleten'}
                            sx={{
                                '& .MuiAutocomplete-popupIndicator': {
                                    color: isDarkMode ? darkTheme.textSecondary : undefined,
                                },
                                '& .MuiAutocomplete-clearIndicator': {
                                    color: isDarkMode ? darkTheme.textSecondary : undefined,
                                },
                                '& .MuiAutocomplete-popupIndicator:hover, & .MuiAutocomplete-clearIndicator:hover': {
                                    color: isDarkMode ? darkTheme.accordionBg : undefined,
                                },
                                '& .MuiAutocomplete-endAdornment svg': {
                                    color: isDarkMode ? darkTheme.textSecondary : grey[700],
                                }
                            }}
                            componentsProps={{
                                paper: {
                                    sx: {
                                        mt: 1,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        boxShadow: isDarkMode ? '0 12px 48px rgba(0, 0, 0, 0.6)' : '0 12px 48px rgba(0, 0, 0, 0.3)',
                                        border: isDarkMode ? `2px solid ${darkTheme.cardBorder}` : '2px solid rgba(0, 0, 0, 0.1)',
                                        bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                                    }
                                },
                                popper: {
                                    sx: { zIndex: 1400 }
                                }
                            }}
                            renderOption={(props, option) => {
                                const displayName = `${option?.lastName || ''}, ${option?.firstName || ''}`.trim().replace(/^,\s*/, '');
                                const isSelected = Boolean(props?.['aria-selected']);

                                return (
                                    <Box
                                        component="li"
                                        {...props}
                                        sx={{
                                            borderRadius: 1.5,
                                            mb: 0.5,
                                            py: 1,
                                            px: 1.25,
                                            mx: 0.5,
                                            bgcolor: isSelected
                                                ? (isDarkMode ? 'rgba(255, 152, 0, 0.16)' : blue[50])
                                                : 'transparent',
                                            border: isSelected
                                                ? (isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.08)')
                                                : '1px solid transparent',
                                            '&.Mui-focused': {
                                                bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.12)' : blue[50],
                                            },
                                            '&:hover': {
                                                bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.12)' : blue[50],
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                            <Avatar
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    bgcolor: getConsistentAvatarColor(option?.email),
                                                    color: isDarkMode ? darkTheme.accordionBg : 'white',
                                                }}
                                            >
                                                <PersonOutlined sx={{ fontSize: 22 }} />
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    noWrap
                                                    sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}
                                                >
                                                    {displayName || option?.email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Athlet suchen..."
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchOutlined sx={{ fontSize: 20, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: params.InputProps.endAdornment,
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2.25,
                                            bgcolor: isDarkMode ? darkTheme.accordionBg : grey[50],
                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            transition: 'border-color 160ms ease, background-color 160ms ease',
                                            '& input': {
                                                color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                '&::placeholder': {
                                                    color: isDarkMode ? darkTheme.textSecondary : undefined,
                                                    opacity: 1,
                                                }
                                            },
                                            '& fieldset': {
                                                borderColor: isDarkMode ? darkTheme.cardBorder : grey[200],
                                            },
                                            '&:hover fieldset': {
                                                borderColor: isDarkMode ? darkTheme.accent : blue[300],
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: isDarkMode ? darkTheme.accent : blue[500],
                                            }
                                        }
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <Button
                        onClick={onDismissAll}
                        disabled={!canDismissAll}
                        variant="outlined"
                        sx={{
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 2.25,
                            px: 1.5,
                            borderColor: isDarkMode ? darkTheme.cardBorder : grey[300],
                            color: isDarkMode ? darkTheme.textPrimary : grey[800],
                            backgroundColor: isDarkMode ? darkTheme.headerBg : grey[50],
                            '&:hover': {
                                borderColor: isDarkMode ? darkTheme.accent : blue[400],
                                backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
                                color: isDarkMode ? darkTheme.accent : blue[800]
                            }
                        }}
                    >
                        Alle verwerfen
                    </Button>
                </Box>

                {loading && (
                    <Typography sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[700] }}>
                        Lädt…
                    </Typography>
                )}

                {!loading && (!filtered || filtered.length === 0) && (
                    <Typography sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[700] }}>
                        Keine neuen Rückmeldungen.
                    </Typography>
                )}

                {!loading && filtered && filtered.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {filtered.map((n, idx) => {
                            const focus = Number(n?.trainingSession?.trainingFocus);
                            const focusColor = (focus >= 1 && focus <= colors.length) ? colors[focus - 1] : (isDarkMode ? orange[500] : blue[600]);
                            const athleteName = `${n?.athlete?.firstName || ''} ${n?.athlete?.lastName || ''}`.trim() || (n?.athlete?.email || '');
                            const trainingDate = n?.trainingSession?.date ? formatDate2(n.trainingSession.date) : '';
                            const notificationDate = n?.feedbackDate ? formatDate2(n.feedbackDate) : '';

                            const notificationRelative = (() => {
                                if (!n?.feedbackDate) return '';
                                const edited = new Date(n.feedbackDate);
                                if (!Number.isFinite(edited.getTime())) return notificationDate;

                                const now = new Date();
                                const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                const startEdited = new Date(edited.getFullYear(), edited.getMonth(), edited.getDate());
                                const diffDays = Math.floor((startToday.getTime() - startEdited.getTime()) / (24 * 60 * 60 * 1000));

                                if (diffDays === 0) return 'Heute';
                                if (diffDays === 1) return 'Gestern';
                                return notificationDate;
                            })();

                            // const createdAtMs = n?.createdAt ? new Date(n.createdAt).getTime() : NaN;
                            // const editedAtMs = n?.feedbackDate ? new Date(n.feedbackDate).getTime() : NaN;
                            // const isUpdated = Number.isFinite(createdAtMs) && Number.isFinite(editedAtMs) && (editedAtMs - createdAtMs) > 1500;
                            // const statusLabel = isUpdated ? 'Aktualisiert' : 'Neu';

                            return (
                                <Card
                                    key={`${n?.feedbackId ?? 'fb'}-${n?.trainingSession?.id ?? 'ts'}-${idx}`}
                                    onClick={() => onOpenNotification?.(n)}
                                    sx={{
                                        borderRadius: '12px',
                                        boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.2s ease',
                                        borderLeft: `5px solid ${focusColor}`,
                                        overflow: 'hidden',
                                        bgcolor: isDarkMode ? darkTheme.accordionBg : 'white',
                                        cursor: onOpenNotification ? 'pointer' : 'default',
                                        '&:hover': {
                                            boxShadow: isDarkMode ? '0 6px 16px rgba(0, 0, 0, 0.6)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                                            transform: 'translateX(4px)'
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 2.25,
                                        gap: 2,
                                    }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    color: isDarkMode ? darkTheme.textPrimary : grey[900],
                                                    fontWeight: 700,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {athleteName}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                sx={{ mt: 0.25, color: isDarkMode ? darkTheme.textSecondary : grey[600], fontSize: '0.85rem', fontWeight: 600 }}
                                            >
                                                {trainingDate ? `vom ${trainingDate}` : ''}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5, color: focusColor, fontWeight: 700 }}
                                            >
                                                {formatTrainingFocus(focus) || 'Einheit'}
                                            </Typography>
                                        </Box>


                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[600], fontSize: '0.85rem', fontWeight: 700, lineHeight: 1.15 }}
                                            >
                                                {notificationRelative}
                                            </Typography>

                                            {/* <Chip
                                                label={statusLabel}
                                                size="small"
                                                sx={{
                                                    height: 22,
                                                    fontWeight: 800,
                                                    letterSpacing: 0.2,
                                                    bgcolor: isUpdated
                                                        ? (isDarkMode ? 'rgba(255, 235, 59, 0.18)' : yellow[50])
                                                        : (isDarkMode ? 'rgba(33, 150, 243, 0.18)' : blue[50]),
                                                    color: isUpdated
                                                        ? (isDarkMode ? yellow[200] : yellow[900])
                                                        : (isDarkMode ? blue[200] : blue[800]),
                                                    border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[300]}`,
                                                }}
                                            /> */}
                                        </Box>
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDismissOne?.(n);
                                            }}
                                            disabled={loading}
                                            sx={{
                                                color: isDarkMode ? darkTheme.textSecondary : grey[700],
                                                border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : `1px solid ${grey[300]}`,
                                                borderRadius: 2,
                                                '&:hover': {
                                                    color: isDarkMode ? darkTheme.accent : blue[800],
                                                    borderColor: isDarkMode ? darkTheme.accent : blue[700],
                                                    backgroundColor: isDarkMode ? darkTheme.cardHover : blue[50],
                                                }
                                            }}
                                        >
                                            <CloseOutlined fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Card>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default FeedbackNotificationsDialog;
